/**
 * KIT Catalogue registry — frontend-static MVP.
 *
 * Maps a department key to its versioned catalogue module. Adding
 * another department later = one new JSON file + one entry here.
 *
 * Later promotion path: when the catalogue moves behind
 * `GET /api/kit/catalogue?department=…`, `getCatalogueForDepartment`
 * switches to `fetch` and callers are unchanged.
 */

import hairMakeup from "./hair_makeup.v1.json";

export const KIT_CATALOGUES = {
    hair_makeup: hairMakeup,
};

/**
 * Return the catalogue for a department key, or `null` if not
 * curated yet. Callers MUST handle the `null` case (KIT surface
 * renders a neutral "coming soon" state).
 */
export function getCatalogueForDepartment(department) {
    if (!department) return null;
    return KIT_CATALOGUES[department] || null;
}

/**
 * Categories sorted by their `order` field. Stable sort — categories
 * sharing an `order` retain their JSON position.
 */
export function getSortedCategories(catalogue) {
    if (!catalogue || !Array.isArray(catalogue.categories)) return [];
    return [...catalogue.categories].sort((a, b) => (a.order || 0) - (b.order || 0));
}

/**
 * Products filtered to a single category. When products carry an
 * optional `order` (integer) — as the Iter-158.13.2.20f.14 canonical
 * manifest does — they are sorted ascending; products without an
 * `order` retain their JSON position (stable sort). MVP renders all
 * products as equal recommendations — the optional `featured` field
 * is reserved for future supervisor-authored highlighting and is
 * NOT surfaced in the current UI.
 */
export function getProductsForCategory(catalogue, categorySlug) {
    if (!catalogue || !Array.isArray(catalogue.products) || !categorySlug) return [];
    const filtered = catalogue.products.filter((p) => p.category_slug === categorySlug);
    // Stable sort by `order` (missing/undefined sorts to the end).
    return filtered
        .map((p, i) => ({ p, i, o: typeof p.order === "number" ? p.order : Number.MAX_SAFE_INTEGER }))
        .sort((a, b) => (a.o - b.o) || (a.i - b.i))
        .map(({ p }) => p);
}

/**
 * Look up a single category descriptor by slug — used by the
 * category detail page for header copy.
 */
export function getCategoryBySlug(catalogue, categorySlug) {
    if (!catalogue || !Array.isArray(catalogue.categories) || !categorySlug) return null;
    return catalogue.categories.find((c) => c.slug === categorySlug) || null;
}
